import { useEffect, useState, useRef } from "react";
import { Navigate, Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import { api, asset } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Store, Plus, Copy, Users, ExternalLink, Upload, KeyRound } from "lucide-react";

export default function Shops() {
  const { user, loading } = useAuth();
  const [data, setData] = useState({ owned: [], member: [] });
  const [creating, setCreating] = useState(false);
  const [joinCode, setJoinCode] = useState("");
  const [form, setForm] = useState({ name: "", description: "", location: "", banner_url: "", logo_url: "" });
  const [busy, setBusy] = useState(false);
  const bannerRef = useRef(null);

  const load = () => api.get("/shops/mine").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { if (user) load(); }, [user]);
  useEffect(() => {
    const j = new URLSearchParams(window.location.search).get("join");
    if (j) setJoinCode(j.toUpperCase());
  }, []);

  if (loading) return <div className="min-h-screen bg-[#0B0E14] text-slate-400 flex items-center justify-center">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;

  const uploadBanner = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/auctions/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, banner_url: data.url }));
      toast.success("Banner uploaded");
    } catch { toast.error("Upload failed"); }
  };

  const createShop = async () => {
    if (form.name.trim().length < 2) return toast.error("Give your shop a name");
    setBusy(true);
    try {
      await api.post("/shops", form);
      toast.success("Storefront created!");
      setForm({ name: "", description: "", location: "", banner_url: "", logo_url: "" });
      setCreating(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't create shop"); }
    finally { setBusy(false); }
  };

  const join = async () => {
    if (!joinCode.trim()) return toast.error("Enter a join code");
    try {
      const { data } = await api.post("/shops/join", { code: joinCode });
      toast.success(`Joined ${data.name} — you can now list under it`);
      setJoinCode("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't join"); }
  };

  const copyInvite = (shop) => {
    const link = `${window.location.origin}/shops?join=${shop.join_code}`;
    navigator.clipboard.writeText(`Join my shop "${shop.name}" on Hustler's Hall — code ${shop.join_code} or ${link}`).then(
      () => toast.success("Invite copied"), () => toast.error("Copy failed"));
  };

  return (
    <div className="min-h-screen bg-[#0B0E14]">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
              <Store className="h-5 w-5 text-amber-400" />
            </div>
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight text-white">Your storefronts</h1>
              <p className="text-sm text-slate-400">Open a shop and let vendors list under your banner — like your own eBay store.</p>
            </div>
          </div>
          <Button data-testid="new-shop-btn" onClick={() => setCreating((v) => !v)} className="bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-full h-10 px-4 shrink-0">
            <Plus className="h-4 w-4 mr-1" /> New shop
          </Button>
        </div>

        {creating && (
          <section className="rounded-xl bg-[#121721] border border-white/10 p-5 space-y-4" data-testid="create-shop-form">
            <Field label="Shop name"><Input data-testid="shop-name-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Joe's Flea Market" className="bg-[#0B0E14] border-white/10 text-white focus-visible:ring-amber-500" /></Field>
            <Field label="Description"><Textarea data-testid="shop-desc-input" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={3} placeholder="Weekend flea market — antiques, tools, oddities." className="bg-[#0B0E14] border-white/10 text-white focus-visible:ring-amber-500 resize-none" /></Field>
            <Field label="Location"><Input data-testid="shop-location-input" value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} placeholder="Austin, TX" className="bg-[#0B0E14] border-white/10 text-white focus-visible:ring-amber-500" /></Field>
            <Field label="Banner image">
              <input ref={bannerRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={uploadBanner} className="hidden" data-testid="shop-banner-file" />
              <div className="flex items-center gap-3">
                <div className="h-16 w-32 rounded-lg overflow-hidden bg-[#0B0E14] border border-white/10 shrink-0">
                  {form.banner_url ? <img src={asset(form.banner_url)} alt="" className="w-full h-full object-cover" /> : null}
                </div>
                <Button type="button" data-testid="shop-banner-btn" onClick={() => bannerRef.current?.click()} variant="outline" className="border-white/15 bg-white/5 text-white hover:bg-white/10 rounded-full h-9">
                  <Upload className="h-4 w-4 mr-1.5" /> Upload banner
                </Button>
              </div>
            </Field>
            <Button data-testid="create-shop-submit" onClick={createShop} disabled={busy} className="bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-full h-11 w-full">
              {busy ? "Creating…" : "Create storefront"}
            </Button>
          </section>
        )}

        {/* Join a shop */}
        <section className="rounded-xl bg-[#121721] border border-white/10 p-5">
          <div className="flex items-center gap-2 text-white font-semibold mb-2"><KeyRound className="h-4 w-4 text-amber-400" /> Join a shop as a vendor</div>
          <p className="text-xs text-slate-500 mb-3">Got a code from a shop owner? Enter it to list your items under their banner.</p>
          <div className="flex gap-2">
            <Input data-testid="join-code-input" value={joinCode} onChange={(e) => setJoinCode(e.target.value)} placeholder="e.g. 7F3A9C" className="bg-[#0B0E14] border-white/10 text-white focus-visible:ring-amber-500 uppercase" />
            <Button data-testid="join-shop-btn" onClick={join} className="bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-full h-10 px-5 shrink-0">Join</Button>
          </div>
        </section>

        {/* Owned shops */}
        {data.owned.map((s) => (
          <section key={s.shop_id} className="rounded-xl bg-[#121721] border border-white/10 overflow-hidden" data-testid={`owned-shop-${s.shop_id}`}>
            <div className="h-24 bg-gradient-to-r from-amber-600/30 to-slate-800 relative">
              {s.banner_url && <img src={asset(s.banner_url)} alt="" className="w-full h-full object-cover" />}
            </div>
            <div className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-white font-bold text-lg">{s.name}</h3>
                  <div className="text-xs text-slate-500 flex items-center gap-2"><Users className="h-3.5 w-3.5" /> {s.member_count} vendor{s.member_count === 1 ? "" : "s"}{s.location ? ` · ${s.location}` : ""}</div>
                </div>
                <Link to={`/shop/${s.slug}`} className="text-amber-400 hover:text-amber-300 text-sm inline-flex items-center gap-1">View store <ExternalLink className="h-3.5 w-3.5" /></Link>
              </div>
              <div className="mt-4 flex items-center gap-2 rounded-lg bg-[#0B0E14] border border-white/10 p-3">
                <span className="text-xs text-slate-500">Vendor join code</span>
                <span className="font-mono text-amber-400 font-bold tracking-widest">{s.join_code}</span>
                <Button data-testid={`copy-invite-${s.shop_id}`} onClick={() => copyInvite(s)} size="sm" variant="outline" className="ml-auto h-8 rounded-full text-xs border-white/15 text-white hover:bg-white/10">
                  <Copy className="h-3.5 w-3.5 mr-1" /> Copy invite
                </Button>
              </div>
            </div>
          </section>
        ))}

        {/* Member shops */}
        {data.member.length > 0 && (
          <section>
            <h2 className="text-sm font-semibold text-slate-400 mb-3">Shops you vend at</h2>
            <div className="space-y-2">
              {data.member.map((s) => (
                <Link key={s.shop_id} to={`/shop/${s.slug}`} className="flex items-center justify-between rounded-xl bg-[#121721] border border-white/10 p-4 hover:border-amber-500/40">
                  <div><div className="text-white font-semibold">{s.name}</div><div className="text-xs text-slate-500">by {s.owner_name}</div></div>
                  <ExternalLink className="h-4 w-4 text-slate-500" />
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-2"><Label className="text-slate-300">{label}</Label>{children}</div>
);
